import { ChevronDown, Clock, Paperclip } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { HtmlContent } from '@/components/ui/html-content';
import { buildLegacyUrl } from '@/lib/legacy-url';
import { cn, formatDateValue, formatNumber } from '@/lib/utils';

import type { FaqItem } from '../faq.types';
import { accentMarkStyle, formatFileSize } from '../lib/faq-format';
import { FaqCategoryIcon } from './faq-category-icon';

interface FaqItemCardProps {
  item: FaqItem;
  abierto: boolean;
  onToggle: () => void;
}

/**
 * Pregunta frecuente en formato acordeón: la respuesta (HTML saneado) sólo se
 * renderiza al abrir, junto con etiquetas y adjuntos.
 */
export function FaqItemCard({ item, abierto, onToggle }: FaqItemCardProps) {
  const triggerId = `faq-trigger-${item.id}`;
  const panelId = `faq-panel-${item.id}`;

  return (
    <Card
      className={cn(
        'overflow-hidden transition-shadow',
        abierto && 'shadow-card ring-1 ring-brand-100',
      )}
    >
      <h3 className="flex">
        <button
          type="button"
          id={triggerId}
          aria-expanded={abierto}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-ink-50/70 focus-visible:ring-2 focus-visible:ring-brand-200 focus-visible:outline-none"
        >
          <FaqCategoryIcon icono={item.categoria_icono} color={item.categoria_color} />

          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-ink-900">{item.pregunta}</span>
              {item.destacado ? <Badge variant="warning">Destacada</Badge> : null}
            </span>

            <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-400">
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="size-1.5 rounded-full bg-ink-300"
                  style={accentMarkStyle(item.categoria_color)}
                />
                {item.categoria_nombre}
              </span>

              {item.actualizado ? (
                <span className="inline-flex items-center gap-1">
                  <Clock className="size-3" aria-hidden="true" />
                  Actualizado el {formatDateValue(item.actualizado)}
                </span>
              ) : null}

              {item.adjuntos.length > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <Paperclip className="size-3" aria-hidden="true" />
                  {item.adjuntos.length}{' '}
                  {item.adjuntos.length === 1 ? 'adjunto' : 'adjuntos'}
                </span>
              ) : null}

              {item.vistas > 0 ? <span>{formatNumber(item.vistas)} vistas</span> : null}
            </span>
          </span>

          <ChevronDown
            aria-hidden="true"
            className={cn(
              'mt-1 size-4 shrink-0 text-ink-400 transition-transform duration-200',
              abierto && 'rotate-180 text-brand-500',
            )}
          />
        </button>
      </h3>

      {abierto ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          className="animate-fade-in border-t border-ink-100 px-5 py-4"
        >
          <HtmlContent html={item.respuesta} />

          {item.tags.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-1.5">
              {item.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  #{tag}
                </Badge>
              ))}
            </div>
          ) : null}

          {item.adjuntos.length > 0 ? (
            <ul className="mt-4 space-y-2">
              {item.adjuntos.map((adjunto) => {
                const href = buildLegacyUrl(adjunto.ruta);

                return (
                  <li key={adjunto.id}>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-2 rounded-control border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 transition-colors hover:border-brand-300 hover:text-brand-700"
                      >
                        <Paperclip className="size-3.5" aria-hidden="true" />
                        {adjunto.nombre}
                        <span className="text-ink-400">{formatFileSize(adjunto.tamano)}</span>
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-2 rounded-control border border-dashed border-ink-200 px-3 py-1.5 text-xs text-ink-500">
                        <Paperclip className="size-3.5" aria-hidden="true" />
                        {adjunto.nombre}
                        <span className="text-ink-400">{formatFileSize(adjunto.tamano)}</span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
